// custom-size-validation.ts — the four custom-size refusals (§8).
//
// THE ORDER IS NORMATIVE, and the 1 m² minimum applies BEFORE the catalogue floor: the amount
// compared against the catalogue is the one that would actually be billed, area floor included,
// never the raw delta. Swapping the two would let custom size look cheaper than it is.
//
// The fourth refusal is not theoretical: double-sided fabric tiers are IRREGULAR, so a custom-size
// price can land below the catalogue price of the very same product. The catalogue price is
// therefore a FLOOR under the custom-size price, and the refusal NAMES the reference to use instead.
//
// A refusal NEVER erases the line: it becomes a validation block, visible and fixable (§4).

import { round2 } from "../core/core";
import { engineMessage } from "../core/messages";
import { engineSettings } from "../../data";
import type { Stand, CustomSizeRate } from "../../models/data.model";
import { deltaM2, miniCustomSizeM2, type DimensionsMm } from "./custom-size";

/**
 * Maximum height beyond the base, in mm (setting `customSizeMaxMarginHeightMm`).
 * @returns The margin in mm.
 */
export function maxMarginHeightMm(): number {
  return engineSettings().parameters.customSizeMaxMarginHeightMm;
}

/**
 * Maximum width beyond the base, in mm (setting `customSizeMaxMarginWidthMm`).
 * @returns The margin in mm.
 */
export function maxMarginWidthMm(): number {
  return engineSettings().parameters.customSizeMaxMarginWidthMm;
}

export type CustomSizeRefusal =
  /** 1. The requested format adds nothing — it already exists as is in the catalogue. (`message` of every refusal: an encoded engine message.) */
  | { code: "zero-delta"; delta: number; message: string }
  /** 2. Height out of range. */
  | { code: "height-out-of-range"; finalHeight: number; maxHeight: number; excess: number; message: string }
  /** 3. Width out of range. */
  | { code: "width-out-of-range"; finalWidth: number; maxWidth: number; excess: number; message: string }
  /** 4. A catalogue reference covers the need and costs MORE: selling it as custom size undercuts it. */
  | { code: "catalogue-floor"; ref: string; name: string; pvCatalogue: number; pvCustomSize: number; gap: number; message: string };

export interface CustomSizeValidationEntry {
  /** NOMINAL dimensions of the parent. */
  base: DimensionsMm;
  /** Requested dimensions. */
  final: DimensionsMm;
  /** Rate derived from the parent. */
  rate: CustomSizeRate;
  /** Tier 1 sale price of the parent — the floor is judged at the FULL rate, never a discounted one. */
  pvParentTier1: number;
  /**
   * Candidate STANDARD references: the catalogue restricted to the SAME component and the SAME
   * number of faces. The caller supplies this subset (the module stays pure and reads nothing).
   */
  candidates: readonly Stand[];
}

/**
 * The cheapest standard reference that covers BOTH requested dimensions.
 * @param final - Requested dimensions.
 * @param candidates - Candidate standard references.
 * @returns The cheapest covering reference, or `null`.
 */
export function findCoveringStand(final: DimensionsMm, candidates: readonly Stand[]): Stand | null {
  const covering = candidates.filter((s) => s.L >= final.L && s.H >= final.H);
  if (!covering.length) return null;
  return covering.reduce((a, b) => (b.pv_tier1 < a.pv_tier1 ? b : a));
}

/**
 * The applicable refusals, IN the normative ORDER. Empty list = the line passes.
 *
 * Check 4 is SKIPPED when check 1 failed: with no extra area, comparing against a catalogue price
 * makes no sense and would add a second message blurring the first.
 * @param entry - Base and final formats, rate, tier 1 price and candidates.
 * @returns The refusals, in normative order.
 */
export function validateCustomSize(entry: CustomSizeValidationEntry): CustomSizeRefusal[] {
  const refusals: CustomSizeRefusal[] = [];
  const delta = deltaM2(entry.base, entry.final);

  // 1. STRICTLY positive delta.
  if (delta <= 0) {
    refusals.push({
      code: "zero-delta", delta: delta,
      message: engineMessage(delta === 0 ? "engineCustomSizeSameAsBase" : "engineCustomSizeSmallerThanBase"),
    });
  }

  // 2. HEIGHT.
  const maxHeight = entry.base.H + maxMarginHeightMm();
  if (entry.final.H > maxHeight) {
    refusals.push({
      code: "height-out-of-range", finalHeight: entry.final.H, maxHeight: maxHeight, excess: entry.final.H - maxHeight,
      message: engineMessage("engineCustomSizeHeightOutOfRange", { height: entry.final.H, max: maxHeight, base: entry.base.H, margin: maxMarginHeightMm() }),
    });
  }

  // 3. WIDTH.
  const maxWidth = entry.base.L + maxMarginWidthMm();
  if (entry.final.L > maxWidth) {
    refusals.push({
      code: "width-out-of-range", finalWidth: entry.final.L, maxWidth: maxWidth, excess: entry.final.L - maxWidth,
      message: engineMessage("engineCustomSizeWidthOutOfRange", { width: entry.final.L, max: maxWidth, base: entry.base.L, margin: maxMarginWidthMm() }),
    });
  }

  // 4. CATALOGUE FLOOR — the 1 m² minimum applies BEFORE, as the normative order requires.
  if (delta > 0) {
    const target = findCoveringStand(entry.final, entry.candidates);
    if (target) {
      const billedDeltaM2 = Math.max(delta, miniCustomSizeM2());
      const pvCustomSize = round2(entry.pvParentTier1 + billedDeltaM2 * entry.rate.pv_m2);
      if (pvCustomSize < target.pv_tier1) {
        refusals.push({
          code: "catalogue-floor",
          ref: target.ref, name: target.name,
          pvCatalogue: round2(target.pv_tier1), pvCustomSize, gap: round2(target.pv_tier1 - pvCustomSize),
          // Amounts are raw euros: the display layer formats them for its locale (`...Eur` parameters).
          message: engineMessage("engineCustomSizeCatalogueFloor", {
            ref: target.ref,
            name: target.name,
            priceEur: target.pv_tier1,
            gapEur: round2(target.pv_tier1 - pvCustomSize),
            customPriceEur: pvCustomSize,
          }),
        });
      }
    }
  }

  return refusals;
}
